import React, { useRef } from 'react';
import { TextField, IconDate, Box, Inline } from 'braid-design-system';

interface DateFieldProps {
    label?: string;
    value?: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
    onlyForToday?: boolean;
    placeholder?: string;
    min?: string;
    max?: string;
}

const DateField = React.forwardRef<HTMLDivElement, DateFieldProps>(
    ({
        label,
        value,
        onChange,
        disabled = false,
        onlyForToday = true,
        placeholder = "YYYY-MM-DD",
        min,
        max,
    }, _ref) => {
        const dateInputRef = useRef<HTMLInputElement>(null);

        // Get today's date in YYYY-MM-DD format
        const getTodayDate = () => {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        const todayDate = getTodayDate();

        const handleIconClick = () => {
            if (!dateInputRef.current) return;
            try {
                dateInputRef.current.showPicker?.();
            } catch {
                dateInputRef.current.click();
            }
        };

        const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            if (newValue) {
                onChange?.(newValue);
            }
        };

        return (
            <Inline space="xxsmall" alignY="center">
                    <TextField
                        type="text"
                        value={value || ''}
                        label={label}
                        placeholder={placeholder}
                        onChange={(e) => {
                            const inputValue = e.currentTarget.value;
                            // Allow empty value
                            if (inputValue === "") {
                                onChange?.("");
                                return;
                            }
                            // Validate date format YYYY-MM-DD
                            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                            if (dateRegex.test(inputValue)) {
                                // Validate if it's a valid date
                                const date = new Date(inputValue);
                                if (!isNaN(date.getTime())) {
                                    // Check min constraint
                                    if (min && inputValue < min) {
                                        return;
                                    }
                                    // Check max constraint
                                    const computedMax = max !== undefined ? max : (onlyForToday ? todayDate : undefined);
                                    if (computedMax && inputValue > computedMax) {
                                        return;
                                    }
                                    onChange?.(inputValue);
                                }
                            } else if (inputValue.length <= 10) {
                                // Allow typing while format is incomplete
                                onChange?.(inputValue);
                            }
                        }}
                        onBlur={() => {
                            if (value) {
                                const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                                if (!dateRegex.test(value)) {
                                    onChange?.("");
                                    return;
                                }
                                if (min && value < min) {
                                    onChange?.("");
                                    return;
                                }
                                const computedMax = max !== undefined ? max : (onlyForToday ? todayDate : undefined);
                                if (computedMax && value > computedMax) {
                                    onChange?.("");
                                    return;
                                }
                            }
                        }}
                        disabled={disabled}
                    />
                {/* Hidden date input for calendar picker */}
                <input
                    ref={dateInputRef}
                    type="date"
                    value={value || ''}
                    min={min}
                    max={max !== undefined ? max : (onlyForToday ? todayDate : undefined)}
                    onChange={handleDateChange}
                    style={{
                        position: 'absolute',
                        opacity: 0,
                        pointerEvents: 'none',
                    }}
                    tabIndex={-1}
                />
                {/* Calendar icon button */}
                {!disabled && (
                    <Box onClick={handleIconClick} cursor='pointer' alignItems={"center"} marginTop={"small"}>
                        <IconDate size='large' tone='info' />
                    </Box>
                )}
            </Inline>
        );
    }
);

DateField.displayName = 'DateField';

export default DateField;

